import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { BACKEND_URL } from './backendConfig';
import ModuleHelp from './ModuleHelp';
import './DevDashboard.css';

// Port of the Apps Script portal's Dashboard tab (Portal.html #dash + WebApp.gs getKpis).
// All numbers come from /dashboard-kpis, which recomputes them from the sample rounds.
const API_BASE = `${BACKEND_URL}/api/development/sample-tracker`;

const DASH = '—';
const val = (v) => (v !== undefined && v !== null && v !== '' ? v : DASH);
// getKpis formats day figures as "5.9d"; nulls stay as an em dash. The unit follows the language
// (i18n.t at render — the component calls useTranslation, so a language switch redraws it).
const days = (v) => {
  const n = Number(v);
  return (v !== undefined && v !== null && v !== '' && Number.isFinite(n))
    ? i18n.t('xCharts.dev.days', { n: n.toFixed(1).replace(/\.0$/, '') })
    : DASH;
};

const KpiCard = ({ label, value, sub, tone }) => (
  <div className={`dd-kpi${tone ? ` ${tone}` : ''}`}>
    <div className="dd-kpi-label">{label}</div>
    <div className="dd-kpi-val">{value}</div>
    <div className="dd-kpi-sub">{sub}</div>
  </div>
);

const Panel = ({ title, children }) => (
  <div className="dd-panel">
    <div className="dd-panel-h">{title}</div>
    {children}
  </div>
);

const StatRow = ({ label, value }) => (
  <div className="dd-stat"><span>{label}</span><span className="dd-stat-val">{value}</span></div>
);

const LocBars = ({ rows }) => {
  const { t } = useTranslation();
  if (!rows || !rows.length) return <div className="dd-empty">{t('dd.noData')}</div>;
  return rows.map((l) => {
    const pct = l.rounds > 0 ? Math.round((l.reworks / l.rounds) * 100) : 0;
    return (
      <div className="dd-loc-row" key={l.name}>
        <span className="dd-loc-name">{l.name}</span>
        <div className="dd-bar"><div className="dd-bar-fill" style={{ width: `${Math.max(4, pct)}%` }}>{pct}%</div></div>
        <span className="dd-loc-sub">{t('xCharts.dev.rounds', { n: l.rounds })}</span>
      </div>
    );
  });
};

const ATTR_ROWS = [
  { key: 'attrHEVN', labelKey: 'xCharts.dev.attrHevn', cls: 'hevn' },
  { key: 'attrHINC', labelKey: 'xCharts.dev.attrHinc', cls: 'hinc' },
  { key: 'attrExt', labelKey: 'xCharts.dev.attrExt', cls: 'ext' },
];

const AttrBars = ({ kpis }) => {
  const { t } = useTranslation();
  const max = Math.max(kpis.attrHEVN || 0, kpis.attrHINC || 0, kpis.attrExt || 0, 1);
  return ATTR_ROWS.map((a) => {
    const v = kpis[a.key] || 0;
    const w = Math.max(v > 0 ? 20 : 4, Math.round((v / max) * 100));
    return (
      <div className="dd-attr-row" key={a.key}>
        <span className="dd-attr-name">{t(a.labelKey)}</span>
        <div className="dd-bar"><div className={`dd-bar-fill ${a.cls}`} style={{ width: `${w}%` }}>{v}</div></div>
      </div>
    );
  });
};

/* THE GUIDE (Max 01/11: every module has one, ending on the manual — the developers' part of the tracker handbook). */
const TOUR = [
  { selector: '.dd-scope', title: 'tourDevDash.scopeT', body: 'tourDevDash.scopeB' },
  { selector: '.dd-kpis', title: 'tourDevDash.kpisT', body: 'tourDevDash.kpisB' },
  { selector: '.dd-panel', title: 'tourDevDash.panelT', body: 'tourDevDash.panelB' },
  { selector: '.dd-table-wrap', title: 'tourDevDash.tableT', body: 'tourDevDash.tableB' },
];

// `embedded` renders it inside the tracker shell (no full-page background / min-height).
function DevDashboard({ embedded = false }) {
  const { t } = useTranslation();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/dashboard-kpis`, { credentials: 'include' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || i18n.t('xCharts.dev.loadFailed'));
        if (!cancelled) { setKpis(data.kpis); setError(''); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const devRows = useMemo(() => (kpis && kpis.byDeveloper) || [], [kpis]);

  const shellClass = `dev-dashboard${embedded ? ' embedded' : ''}`;

  if (loading && !kpis) {
    return (
      <div className={shellClass}>
        <div className="dd-wrap"><div className="dd-loading">{t('dd.loading')}</div></div>
      </div>
    );
  }
  if (error && !kpis) {
    return (
      <div className={shellClass}>
        <div className="dd-wrap">
          <div className="dd-error">{t('xCharts.dev.loadError', { error })}</div>
          <button type="button" className="dd-btn" onClick={() => setReloadKey((k) => k + 1)}>{t('dd.retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <div className="dd-wrap">
        <div className="dd-title-row mh-titlerow">
          <h2 className="dd-title">{t('dd.title')}</h2>
          <ModuleHelp manual="sample" steps={TOUR} tourKey="devDashboard" />
        </div>
        {/* The Sample Tracker lists 215 samples; this page counts 214. Same word, different number,
            and until this line there was nothing on either screen to say why — the trap Miley hit on
            Product Line, where a card read 8,835 over a table of 4,078 and the screen had no answer.
            Trial samples are excluded here on Jay's point: rework and speed mean nothing for a master
            pattern or an RM test. */}
        <div className="dd-scope">{t('dd.scopeNote')}</div>

        <div className="dd-kpis">
          <KpiCard label={t('dd.totalRounds')} value={val(kpis.totalRounds)} sub={t('dd.acrossSamples', { count: val(kpis.totalProducts) })} />
          <KpiCard label={t('dd.avgRoundsSample')} value={val(kpis.avgRounds)} sub={t('dd.higherSpinning')} />
          <KpiCard label={t('dd.reworkRatio')} value={val(kpis.reworkRate)} sub={t('dd.mainKpi')} tone="red" />
          <KpiCard label={t('dd.pastDue')} value={val(kpis.pastDeadline)} sub={t('dd.pastDueSub', {
            /* The server always sends this same English phrase; it is shown in the reader's language.
               Any other unit it might send one day is printed as it comes. */
            unit: (!kpis.pastDeadlineUnit || kpis.pastDeadlineUnit === 'samples by Base ID')
              ? t('xCharts.dev.unitSamplesByBase') : kpis.pastDeadlineUnit,
          })} tone="orange" />
        </div>

        <div className="dd-grid2">
          <Panel title={t('dd.reworkByRoom')}>
            <LocBars rows={kpis.byLocation} />
          </Panel>
          <Panel title={t('dd.reworkAttribution')}>
            <AttrBars kpis={kpis} />
            <div className="dd-panel-foot">
              <span>{t('dd.hevnRatio')}</span>
              <span className="dd-panel-foot-val">{kpis.hevnAttrRatio !== undefined ? `${kpis.hevnAttrRatio}%` : DASH}</span>
            </div>
          </Panel>
        </div>

        <div className="dd-grid2">
          <Panel title={t('dd.stuckTime')}>
            <div className="dd-stats">
              <StatRow label={t('dd.avgCommentDiffers')} value={days(kpis.avgCmtDiffers)} />
              <StatRow label={t('dd.avgCommentAdvance')} value={days(kpis.avgCmtAdvance)} />
              <StatRow label={t('dd.avgCommentAll')} value={days(kpis.avgCmtDays)} />
              <StatRow label={t('dd.spinning')} value={val(kpis.spinningProducts)} />
            </div>
          </Panel>
          <Panel title={t('dd.sampleSpeed')}>
            <div className="dd-note">
              {t('dd.sampleSpeedNote')}
            </div>
            <div className="dd-stats">
              <StatRow label={t('dd.avgMakeHevn')} value={days(kpis.avgMakeDaysHEVN)} />
              <StatRow label={t('dd.avgMakeTs')} value={days(kpis.avgMakeDaysTS)} />
              <StatRow label={t('dd.avgMakeMs')} value={days(kpis.avgMakeDaysMS)} />
              <StatRow label={t('dd.avgMakeTotal')} value={days(kpis.avgMakeDays)} />
            </div>
          </Panel>
        </div>

        <Panel title={t('dd.byDeveloper')}>
          <div className="dd-table-wrap">
            <table className="dd-table">
              <thead>
                <tr>
                  <th>{t('dd.colDeveloper')}</th><th>{t('dd.colSamples')}</th><th>{t('dd.colRounds')}</th><th>{t('dd.colRework')}</th>
                  <th>{t('dd.colReworkRate')}</th><th>{t('dd.colAvgRounds')}</th><th>{t('dd.colAvgMakeDays')}</th><th>{t('dd.colPending')}</th>
                </tr>
              </thead>
              <tbody>
                {devRows.length ? devRows.map((d) => (
                  <tr key={d.name}>
                    <td><b>{d.name}</b></td>
                    <td>{d.products || 0}</td>
                    <td>{d.rounds || 0}</td>
                    <td>{d.reworks || 0}</td>
                    {/* the web app paints a rate above 10% red */}
                    <td className={parseFloat(d.rate) > 10 ? 'dd-red' : ''}>{val(d.rate)}</td>
                    <td>{val(d.avgRounds)}</td>
                    <td>{days(d.avgMakeDays)}</td>
                    <td>{val(d.pending)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={8} className="dd-empty-cell">{t('dd.noData')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

      </div>
    </div>
  );
}

export default DevDashboard;
